import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const KEY = "novaimmo-cookie-consent";

export const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setVisible(true);
  }, []);

  // Reopen the banner from the footer "Manage cookies" link
  useEffect(() => {
    const open = () => {
      setVisible(true);
      setCollapsed(false);
    };
    window.addEventListener("novaimmo:open-cookie-banner", open);
    return () => window.removeEventListener("novaimmo:open-cookie-banner", open);
  }, []);

  const choose = (value) => {
    localStorage.setItem(KEY, value);
    // Notifie l'app (ex. chargement de la balise Google Ads si "accepted")
    window.dispatchEvent(new CustomEvent("novaimmo:cookie-consent", { detail: value }));
    setVisible(false);
    setCollapsed(false);
  };

  return (
    <AnimatePresence>
      {visible && collapsed && (
        <motion.button
          key="cookie-chip"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.7 }}
          transition={{ duration: 0.3 }}
          onClick={() => setCollapsed(false)}
          aria-label={t("cookie.more")}
          data-testid="cookie-banner-chip"
          className="fixed bottom-4 left-4 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#2E8BFF] shadow-[0_8px_24px_rgba(17,24,39,0.18)] border border-[#111827]/8 transition-transform duration-300 hover:scale-110"
        >
          <Cookie className="h-5 w-5" />
        </motion.button>
      )}
      {visible && !collapsed && (
        <motion.div
          key="cookie-banner"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-3 left-3 z-40 w-[min(360px,calc(100vw-24px))] sm:bottom-6 sm:left-auto sm:right-6 sm:w-full sm:max-w-md [@media(max-height:500px)]:bottom-3 [@media(max-height:500px)]:left-3 [@media(max-height:500px)]:right-auto [@media(max-height:500px)]:w-[300px] [@media(max-height:500px)]:max-w-[300px]"
          data-testid="cookie-banner"
        >
          <div className="max-h-[70vh] overflow-y-auto rounded-2xl border border-[#111827]/8 bg-white/95 p-4 shadow-[0_16px_48px_rgba(17,24,39,0.18)] backdrop-blur-xl sm:p-5">
            <div className="flex items-start gap-3">
              <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2E8BFF]/10 text-[#2E8BFF] sm:inline-flex">
                <Cookie className="h-4 w-4" />
              </span>
              <p className="text-[13px] leading-relaxed text-[#111827]/80 sm:text-sm">
                {t("cookie.text")}{" "}
                <Link to="/politique-cookies" data-testid="cookie-more-link" className="font-medium text-[#2E8BFF] underline underline-offset-2">
                  {t("cookie.more")}
                </Link>
              </p>
              <button
                onClick={() => setCollapsed(true)}
                aria-label={t("cookie.later")}
                data-testid="cookie-collapse-btn"
                className="ml-auto shrink-0 rounded-full p-1 text-slate-400 transition-colors duration-300 hover:bg-slate-100 hover:text-[#111827]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex justify-end gap-2 sm:mt-4 sm:gap-2.5">
              <button
                data-testid="cookie-refuse-btn"
                onClick={() => choose("refused")}
                className="rounded-xl border border-[#111827]/15 px-4 py-1.5 text-[13px] font-medium text-[#111827]/70 transition-colors duration-300 hover:border-[#111827]/40 hover:text-[#111827] sm:px-5 sm:py-2 sm:text-sm"
              >
                {t("cookie.refuse")}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => choose("accepted")}
                className="rounded-xl shadow-sm bg-[#2E8BFF] px-4 py-1.5 text-[13px] font-semibold text-white transition-transform duration-300 hover:scale-105 sm:px-5 sm:py-2 sm:text-sm"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
